import { formatContactName, getGradeLabel } from '@car-pool/shared/schoolRecords';
import { getContactEmailDisplay } from '@car-pool/shared/contactIdentity';
import SchoolAvatar from './SchoolAvatar';
import SchoolInfoRow from './SchoolInfoRow';

function formatLastLogin(value) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString([], {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export default function ParentProfileCard({
  parent,
  linkedStudents = [],
  dismissalByStudentId = {},
  className = '',
}) {
  if (!parent) return null;

  // A driver-invite placeholder is not shown as an address; the card prompts
  // for a real one instead.
  const emailDisplay = getContactEmailDisplay(parent);

  const classes = ['sap-parent-profile-card', className].filter(Boolean).join(' ');
  const sentToCarpoolCount = linkedStudents.filter((student) => dismissalByStudentId[student.id]?.status === 'sent').length;

  return (
    <div className={classes}>
      <div className="sap-parent-hero">
        <SchoolAvatar
          name={formatContactName(parent)}
          seed={parent.id}
          photoPath={parent.photo_relative_path}
          enableViewer
          large
        />
        <h3>{formatContactName(parent)}</h3>
        <span>{parent.phone || 'No phone number'}</span>
        <span className={emailDisplay.needsEmail ? 'sap-needs-email' : ''}>{emailDisplay.label}</span>
      </div>

      <div className="sap-section-card">
        <div className="sap-section-caption">Sent to Carpool ({sentToCarpoolCount}/{linkedStudents.length})</div>
        {linkedStudents.length === 0 ? (
          <div className="sap-section-empty">No linked students yet.</div>
        ) : (
          linkedStudents.map((student) => {
            const isSentToCarpool = dismissalByStudentId[student.id]?.status === 'sent';
            return (
              <div key={student.id} className="sap-student-row">
                <div className="sap-student-copy">
                  <SchoolAvatar record={student} />
                  <div>
                    <div className="sap-student-name">{student.f_name} {student.l_name}</div>
                    <div className="sap-student-grade">{getGradeLabel(student.grade, {}, { short: true })}</div>
                  </div>
                </div>
                <div className={`sap-status-dot ${isSentToCarpool ? 'active' : ''}`}>
                  {isSentToCarpool ? '✓' : ''}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="sap-section-card">
        <div className="sap-section-caption">Vehicle Information</div>
        <SchoolInfoRow label="Make" value={parent.vehicle_make_model} />
        <SchoolInfoRow label="Color" value={parent.car_color} />
        <SchoolInfoRow label="License Plate" value={parent.license_plate} />
        <SchoolInfoRow label="Driver License" value={parent.driver_license_number} />
      </div>

      <div className="sap-section-card">
        <div className="sap-section-caption">Emergency Contact Information</div>
        <SchoolInfoRow
          label="Name"
          value={[parent.emergency_f_name, parent.emergency_l_name].filter(Boolean).join(' ')}
        />
        <SchoolInfoRow label="Phone" value={parent.emergency_backup_phone} />
        <SchoolInfoRow label="Relationship" value={parent.emergency_relationship} />
        <SchoolInfoRow
          label="Address"
          value={[
            parent.street_address,
            [parent.city, parent.state].filter(Boolean).join(', '),
            parent.zip_code,
          ].filter(Boolean).join(' ')}
        />
      </div>

      <div className="sap-section-card">
        <div className="sap-section-caption">Misc</div>
        <SchoolInfoRow label="Last Login" value={formatLastLogin(parent.last_login_at)} />
        <SchoolInfoRow label="Password Set" value={parent.has_password ? 'Yes' : 'No'} />
      </div>
    </div>
  );
}
